import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import {
  TALK_EVENTS_QUERY_FOR_PRACTITIONERS_OTHER_THAN,
  TALK_EXISTING_EVENT_REQUESTS_CHECK_QUERY,
} from 'Services/BeGateway/Talk/talk.services';
import { talkEventRequestsQueryForPractitionersOtherThan } from 'Services/BeGateway/Talk/__generated__/talkEventRequestsQueryForPractitionersOtherThan';
import { talkEventsQueryForPractitionersOtherThan } from 'Services/BeGateway/Talk/__generated__/talkEventsQueryForPractitionersOtherThan';
import { USER_QUERY } from 'Services/User/user.services';
import { BEGatewayQueryContext, defaultQueryConfig } from 'utils/apollo';
import { userQuery } from 'Services/User/__generated__/userQuery';

/**
 * Checks whether the current user has an upcoming session with a different practitioner
 * than `practitionerId`, or any active booking request regardless of practitioner.
 */
export default (practitionerId: string) => {
  const {
    data: userData,
    loading: userQueryLoading,
    error: userQueryError,
  } = useQuery<userQuery>(USER_QUERY);
  const now = useMemo(() => new Date(), []);
  const { id: userID } = userData?.user || {};
  const commonVariables = {
    first: 1,
    after: null,
    unmindUserId: userID ?? '',
  };

  const {
    data: events,
    loading: eventsLoading,
    error: eventsError,
  } = useQuery<talkEventsQueryForPractitionersOtherThan>(
    TALK_EVENTS_QUERY_FOR_PRACTITIONERS_OTHER_THAN,
    {
      variables: {
        ...commonVariables,
        filter: {
          where: {
            endsAt: {
              moreThan: now.toISOString(),
            },
            userId: {
              notEqualTo: practitionerId,
            },
          },
        },
      },
      skip: !userID,
      ...defaultQueryConfig,
      ...BEGatewayQueryContext,
    },
  );

  const {
    data: eventRequests,
    loading: eventRequestsLoading,
    error: eventRequestsError,
  } = useQuery<talkEventRequestsQueryForPractitionersOtherThan>(
    TALK_EXISTING_EVENT_REQUESTS_CHECK_QUERY,
    {
      variables: {
        ...commonVariables,
      },
      skip: !userID,
      ...defaultQueryConfig,
      ...BEGatewayQueryContext,
    },
  );

  const hasBooking = !!events?.unmindEvents?.edges?.length;
  const hasRequest = !!eventRequests?.unmindEventRequests?.edges?.length;

  return {
    loading: eventsLoading || eventRequestsLoading || userQueryLoading,
    hasBookingOrRequest: hasBooking || hasRequest,
    errors: [eventRequestsError, eventsError, userQueryError],
  };
};
